import { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { Check, ExternalLink } from 'lucide-react'
import UiButton from '../components/ui/UiButton'
import { getUserSubscriptions } from '../api/library'
import { trackEvent } from '../utils/metaPixel'

const PREFERENCES_FORM_URL =
  'https://docs.google.com/forms/d/e/1FAIpQLSfp1Fahjp79cpUKGz_1HRqZIpyaILPsppJ2qLn1BvTjuCvVMA/viewform'

function buildPurchaseParams(subscriptions) {
  if (!subscriptions || subscriptions.length === 0) return null

  const contentIds = []
  const contentNames = []
  let numItems = 0

  subscriptions.forEach((sub) => {
    // The backend currently returns one user-facing subscription per tier,
    // without price/quantity. We track the slugs and names; value can be
    // added once the backend exposes tier prices here.
    contentIds.push(sub.slug)
    contentNames.push(sub.displayName || sub.name)
    numItems += 1
  })

  return {
    content_ids: contentIds,
    content_name: contentNames.join(', '),
    content_type: 'product',
    currency: 'PLN',
    num_items: numItems,
  }
}

export default function CheckoutSuccessPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const purchaseTracked = useRef(false)

  const redirectUrl = searchParams.get('redirectUrl')
  const subscriptionSlug = searchParams.get('subscription')
  const subscriptionCategory = searchParams.get('category')
  const allSubscriptions = (searchParams.get('subscriptions') || subscriptionSlug || '')
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean)
  const allCategories = (searchParams.get('categories') || subscriptionCategory || '')
    .split(',')
    .map((c) => c.trim())
    .filter(Boolean)
  const isPersonal =
    allSubscriptions.includes('personal') || allCategories.includes('personal') ||
    subscriptionSlug === 'personal' || subscriptionCategory === 'personal'

  useEffect(() => {
    // Legacy intermediate-redirect case: if this page is opened with a
    // redirectUrl, send the user straight to Stripe. Normally CheckoutPage
    // redirects directly and this page is only shown after Stripe returns
    // the user to the site.
    if (redirectUrl) {
      window.location.href = redirectUrl
    }
  }, [redirectUrl])

  useEffect(() => {
    if (purchaseTracked.current) return
    purchaseTracked.current = true

    getUserSubscriptions()
      .then((subscriptions) => {
        const params = buildPurchaseParams(subscriptions)
        if (params) {
          trackEvent('Purchase', params)
        }
      })
      .catch(() => {
        // Fire a lightweight Purchase event even if the subscription lookup fails.
        trackEvent('Purchase', {
          content_ids: allSubscriptions,
          content_type: 'product',
          currency: 'PLN',
        })
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (redirectUrl) {
    return (
      <div className="max-w-xl mx-auto text-center bg-brand-cream/95 rounded-3xl p-10 shadow-xl">
        <p className="text-brand-blue">{t('checkout.redirecting')}</p>
      </div>
    )
  }

  return (
    <div className="max-w-xl mx-auto text-center bg-brand-cream/95 rounded-3xl p-10 shadow-xl">
      <Check size={64} className="mx-auto text-green-600 mb-4" />
      <h2 className="font-display text-3xl text-brand-blue mb-3">
        {t('checkout.successTitle')}
      </h2>
      <p className="text-gray-700 mb-6">{t('checkout.successMessage')}</p>

      {isPersonal && (
        <div className="bg-brand-blue/5 rounded-2xl p-6 mb-6">
          <p className="text-brand-blue mb-4">
            {t('checkout.personalPreferencesMessage')}
          </p>
          <a
            href={PREFERENCES_FORM_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center gap-2 bg-brand-gold text-brand-blue rounded-full px-6 py-3 font-bold hover:bg-brand-gold/90 transition"
          >
            <ExternalLink size={18} />
            {t('checkout.personalPreferencesButton')}
          </a>
        </div>
      )}

      <UiButton type="button" onClick={() => navigate('/moja-biblioteka')}>
        {t('common.goToLibrary')}
      </UiButton>
    </div>
  )
}
